import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Send, FileText, Eye, Trash2, Check, X, Clock, Plus, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  new: { label: 'Neu', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In Bearbeitung', color: 'bg-yellow-100 text-yellow-700' },
  offer_sent: { label: 'Offerte gesendet', color: 'bg-purple-100 text-purple-700' },
  accepted: { label: 'Angenommen', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Abgelehnt', color: 'bg-red-100 text-red-700' },
  closed: { label: 'Geschlossen', color: 'bg-gray-100 text-gray-700' },
};

const offerStatusConfig = {
  draft: { label: 'Entwurf', color: 'bg-gray-100 text-gray-700' },
  sent: { label: 'Gesendet', color: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Angenommen', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Abgelehnt', color: 'bg-red-100 text-red-700' },
  expired: { label: 'Abgelaufen', color: 'bg-orange-100 text-orange-700' },
  paid: { label: 'Bezahlt', color: 'bg-emerald-100 text-emerald-700' },
};

export default function AdminInquiries() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('inquiries');
  const [inquiries, setInquiries] = useState([]);
  const [offers, setOffers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showNewOfferDialog, setShowNewOfferDialog] = useState(false);
  const [loading, setLoading] = useState(true);

  const [offerForm, setOfferForm] = useState({
    inquiry_id: '', customer_email: '', customer_name: '', location_id: '',
    start_date: '', end_date: '', price: '', notes: '', valid_until: '',
  });

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { loadData(); }, [token, statusFilter]);

  const loadData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [inqRes, offRes, locRes] = await Promise.all([
        fetch(`${API}/api/admin/inquiries?status=${statusFilter}`, { headers }),
        fetch(`${API}/api/admin/offers?status=${statusFilter}`, { headers }),
        fetch(`${API}/api/locations`),
      ]);
      setInquiries(await inqRes.json());
      setOffers(await offRes.json());
      setLocations(await locRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const updateInquiryStatus = async (id, status, notes = '') => {
    try {
      const res = await fetch(`${API}/api/admin/inquiries/${id}/status`, {
        method: 'PUT', headers, body: JSON.stringify({ status, admin_notes: notes }),
      });
      if (res.ok) { toast.success('Status aktualisiert'); loadData(); }
      else toast.error('Fehler');
    } catch (e) { toast.error('Netzwerkfehler'); }
  };

  const openOfferFromInquiry = (inq) => {
    setSelectedInquiry(inq);
    setOfferForm({
      inquiry_id: inq.id,
      customer_email: inq.customer_email,
      customer_name: inq.customer_name,
      location_id: inq.location_id || '',
      start_date: inq.desired_start_date || '',
      end_date: inq.desired_end_date || '',
      price: '', notes: '',
      valid_until: new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0],
    });
    setShowOfferDialog(true);
  };

  const openNewOffer = () => {
    setSelectedInquiry(null);
    setOfferForm({
      inquiry_id: '', customer_email: '', customer_name: '', location_id: '',
      start_date: '', end_date: '', price: '', notes: '',
      valid_until: new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0],
    });
    setShowNewOfferDialog(true);
  };

  const createOffer = async () => {
    if (!offerForm.customer_email || !offerForm.location_id || !offerForm.price) {
      toast.error('Bitte füllen Sie E-Mail, Standort und Preis aus.');
      return;
    }
    try {
      const payload = {
        ...offerForm,
        price: parseFloat(offerForm.price),
        start_date: offerForm.start_date ? new Date(offerForm.start_date).toISOString() : '',
        end_date: offerForm.end_date ? new Date(offerForm.end_date).toISOString() : '',
        valid_until: offerForm.valid_until ? new Date(offerForm.valid_until).toISOString() : undefined,
      };
      const res = await fetch(`${API}/api/admin/offers`, {
        method: 'POST', headers, body: JSON.stringify(payload),
      });
      if (res.ok) {
        toast.success('Offerte erstellt!');
        setShowOfferDialog(false);
        setShowNewOfferDialog(false);
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Fehler');
      }
    } catch (e) { toast.error('Netzwerkfehler'); }
  };

  const sendOffer = async (offerId) => {
    try {
      const res = await fetch(`${API}/api/admin/offers/${offerId}/send`, {
        method: 'POST', headers,
      });
      if (res.ok) { toast.success('Offerte gesendet!'); loadData(); }
      else { const e = await res.json(); toast.error(e.detail || 'Fehler beim Senden'); }
    } catch (e) { toast.error('Netzwerkfehler'); }
  };

  const deleteOffer = async (offerId) => {
    if (!window.confirm('Offerte wirklich löschen?')) return;
    try {
      const res = await fetch(`${API}/api/admin/offers/${offerId}`, { method: 'DELETE', headers });
      if (res.ok) { toast.success('Offerte gelöscht'); loadData(); }
      else toast.error('Fehler');
    } catch (e) { toast.error('Netzwerkfehler'); }
  };

  const OfferFormFields = () => (
    <div className="space-y-4 mt-2">
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Kundenname *</Label><Input data-testid="offer-name" value={offerForm.customer_name} onChange={e => setOfferForm(p => ({ ...p, customer_name: e.target.value }))} /></div>
        <div><Label>Kunden-E-Mail *</Label><Input data-testid="offer-email" type="email" value={offerForm.customer_email} onChange={e => setOfferForm(p => ({ ...p, customer_email: e.target.value }))} /></div>
      </div>
      <div>
        <Label>Standort *</Label>
        <Select value={offerForm.location_id} onValueChange={v => setOfferForm(p => ({ ...p, location_id: v }))}>
          <SelectTrigger data-testid="offer-location"><SelectValue placeholder="Standort wählen..." /></SelectTrigger>
          <SelectContent>{locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name} – CHF {l.price_per_week}/Wo</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Startdatum</Label><Input data-testid="offer-start" type="date" value={offerForm.start_date} onChange={e => setOfferForm(p => ({ ...p, start_date: e.target.value }))} /></div>
        <div><Label>Enddatum</Label><Input data-testid="offer-end" type="date" value={offerForm.end_date} onChange={e => setOfferForm(p => ({ ...p, end_date: e.target.value }))} /></div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Preis (CHF) *</Label><Input data-testid="offer-price" type="number" value={offerForm.price} onChange={e => setOfferForm(p => ({ ...p, price: e.target.value }))} placeholder="z.B. 1400" /></div>
        <div><Label>Gültig bis</Label><Input data-testid="offer-valid" type="date" value={offerForm.valid_until} onChange={e => setOfferForm(p => ({ ...p, valid_until: e.target.value }))} /></div>
      </div>
      <div><Label>Anmerkungen</Label><Textarea data-testid="offer-notes" value={offerForm.notes} onChange={e => setOfferForm(p => ({ ...p, notes: e.target.value }))} placeholder="z.B. Spezialkonditionen..." rows={3} /></div>
      <Button data-testid="create-offer-btn" onClick={createOffer} className="w-full"><FileText className="w-4 h-4 mr-2" />Offerte erstellen</Button>
    </div>
  );

  const tabs = [
    { id: 'inquiries', label: 'Anfragen', count: inquiries.length },
    { id: 'offers', label: 'Offerten', count: offers.length },
  ];

  return (
    <div data-testid="admin-inquiries">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Anfragen & Offerten</h1>
        <Button onClick={openNewOffer} data-testid="new-offer-btn"><Plus className="w-4 h-4 mr-2" />Neue Offerte</Button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex gap-1 bg-[hsl(var(--surface-2))] p-1 rounded-lg">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`admin-tab-${t.id}`}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === t.id ? 'bg-white shadow text-[hsl(var(--foreground))]' : 'text-[hsl(var(--muted-foreground))]'}`}>
              {t.label} <span className="text-xs opacity-60">({t.count})</span>
            </button>
          ))}
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            {Object.entries(activeTab === 'inquiries' ? statusConfig : offerStatusConfig).map(([k, v]) => (
              <SelectItem key={k} value={k}>{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* INQUIRIES TAB */}
      {activeTab === 'inquiries' && (
        <div className="space-y-3">
          {loading ? <p>Laden...</p> : inquiries.length === 0 ? (
            <Card><CardContent className="py-12 text-center"><p className="text-[hsl(var(--muted-foreground))]">Keine Anfragen gefunden.</p></CardContent></Card>
          ) : inquiries.map(inq => {
            const s = statusConfig[inq.status] || statusConfig.new;
            return (
              <Card key={inq.id} data-testid={`admin-inquiry-${inq.id}`}>
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{inq.subject}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>
                      </div>
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">{inq.message?.substring(0, 200)}</p>
                      <div className="flex gap-4 mt-2 text-sm text-[hsl(var(--muted-foreground))]">
                        <span><strong>Kunde:</strong> {inq.customer_name} ({inq.customer_email})</span>
                        {inq.customer_phone && <span><strong>Tel:</strong> {inq.customer_phone}</span>}
                        {inq.customer_company && <span><strong>Firma:</strong> {inq.customer_company}</span>}
                      </div>
                      {inq.location && <p className="text-sm mt-1"><strong>Standort:</strong> {inq.location.name}</p>}
                      {inq.desired_start_date && <p className="text-sm text-[hsl(var(--muted-foreground))]">Wunsch: {inq.desired_start_date} – {inq.desired_end_date}</p>}
                      <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{new Date(inq.created_at).toLocaleString('de-CH')}</p>
                    </div>
                    <div className="flex flex-col gap-2 min-w-[140px]">
                      {inq.status === 'new' && (
                        <Button size="sm" onClick={() => updateInquiryStatus(inq.id, 'in_progress')} data-testid={`inquiry-progress-${inq.id}`}>
                          <Clock className="w-3 h-3 mr-1" />Bearbeiten
                        </Button>
                      )}
                      {['new', 'in_progress'].includes(inq.status) && (
                        <Button size="sm" variant="default" onClick={() => openOfferFromInquiry(inq)} data-testid={`inquiry-offer-${inq.id}`}>
                          <FileText className="w-3 h-3 mr-1" />Offerte
                        </Button>
                      )}
                      {['new', 'in_progress'].includes(inq.status) && (
                        <Button size="sm" variant="destructive" onClick={() => updateInquiryStatus(inq.id, 'rejected')} data-testid={`inquiry-reject-${inq.id}`}>
                          <X className="w-3 h-3 mr-1" />Ablehnen
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* OFFERS TAB */}
      {activeTab === 'offers' && (
        <div className="space-y-3">
          {loading ? <p>Laden...</p> : offers.length === 0 ? (
            <Card><CardContent className="py-12 text-center"><p className="text-[hsl(var(--muted-foreground))]">Keine Offerten gefunden.</p></CardContent></Card>
          ) : offers.map(offer => {
            const s = offerStatusConfig[offer.status] || offerStatusConfig.draft;
            const total = (offer.price || 0) + (offer.services_price || 0);
            return (
              <Card key={offer.id} data-testid={`admin-offer-${offer.id}`}>
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{offer.customer_name}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>
                      </div>
                      <p className="text-sm"><strong>Standort:</strong> {offer.location?.name || '—'}</p>
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">
                        {new Date(offer.start_date).toLocaleDateString('de-CH')} – {new Date(offer.end_date).toLocaleDateString('de-CH')}
                      </p>
                      {offer.notes && <p className="text-sm italic mt-1">"{offer.notes}"</p>}
                      <p className="text-sm text-[hsl(var(--muted-foreground))]">E-Mail: {offer.customer_email}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2 min-w-[160px]">
                      <span className="text-xl font-bold text-[hsl(var(--primary))]">CHF {total.toFixed(2)}</span>
                      {offer.valid_until && <span className="text-xs text-[hsl(var(--muted-foreground))]">Gültig bis: {new Date(offer.valid_until).toLocaleDateString('de-CH')}</span>}
                      <div className="flex gap-2 mt-2 flex-wrap">
                        <Button size="sm" variant="outline" onClick={async () => {
                          try {
                            const res = await fetch(`${API}/api/admin/offers/${offer.id}/pdf`, {
                              headers: { 'Authorization': `Bearer ${token}` }
                            });
                            if (res.ok) {
                              const blob = await res.blob();
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `Offerte_${offer.id.slice(0,8).toUpperCase()}.pdf`;
                              a.click();
                              URL.revokeObjectURL(url);
                            } else { toast.error('Fehler beim PDF-Download'); }
                          } catch(e) { toast.error('Netzwerkfehler'); }
                        }} data-testid={`download-pdf-${offer.id}`}>
                          <Download className="w-3 h-3 mr-1" />PDF
                        </Button>
                        {offer.status === 'draft' && (
                          <Button size="sm" onClick={() => sendOffer(offer.id)} data-testid={`send-offer-${offer.id}`}>
                            <Send className="w-3 h-3 mr-1" />Senden mit PDF
                          </Button>
                        )}
                        {!['accepted', 'paid'].includes(offer.status) && (
                          <Button size="sm" variant="destructive" onClick={() => deleteOffer(offer.id)} data-testid={`delete-offer-${offer.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* OFFER FROM INQUIRY DIALOG */}
      <Dialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Offerte erstellen{selectedInquiry ? ` – ${selectedInquiry.subject}` : ''}</DialogTitle></DialogHeader>
          <OfferFormFields />
        </DialogContent>
      </Dialog>

      {/* NEW OFFER DIALOG */}
      <Dialog open={showNewOfferDialog} onOpenChange={setShowNewOfferDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Neue Offerte erstellen</DialogTitle></DialogHeader>
          <OfferFormFields />
        </DialogContent>
      </Dialog>
    </div>
  );
}
